import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringMenuItem } from '../../../../core/models/catering.model';
import { LocaleService } from '../../../../core/services/locale.service';
import { formatCurrencyMinor, DIETARY_LABELS } from '../../../../core/utils/catering';
import { PaperButtonComponent } from '@ricoh-paper/angular';
import { DietaryBadgesComponent } from '../dietary-badges/dietary-badges.component';

/**
 * ItemCustomiseDialogComponent — McDonald's/UberEats-style item configuration.
 *
 * Opens as an overlay when adding an item that has custom questions.
 * User selects options (milk type, size, filling, etc.) and quantity,
 * then confirms. Each unique option set becomes a separate cart line.
 *
 * "Apply to all" mode: if qty > 1, all copies get the same options.
 */
@Component({
  selector: 'ct-item-customise',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DietaryBadgesComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    <div class="icd__backdrop" (click)="onCancel()"></div>
    <div class="icd">
      <!-- Hero image -->
      @if (item().image) {
        <div class="icd__hero">
          <img [src]="item().image" [alt]="item().name" loading="lazy"
               (error)="$any($event.target).style.display='none'" />
        </div>
      }

      <div class="icd__body">
        <!-- Item info -->
        <h3 class="icd__name">{{ item().translatedName || item().name }}</h3>
        @if (item().description) {
          <p class="icd__desc">{{ item().translatedDescription || item().description }}</p>
        }
        <div class="icd__price-row">
          @if (item().costPence) {
            <span class="icd__price">{{ fmtPrice(item().costPence!) }}</span>
          }
          @if (item().dietaryTags.length) {
            <ct-dietary-badges [tags]="item().dietaryTags" />
          }
        </div>

        <!-- Custom questions -->
        @for (q of item().questions; track q.name) {
          <div class="icd__question">
            <label class="icd__q-label">
              {{ q.name }}
              @if (q.required) { <span class="icd__required">*</span> }
            </label>

            @if ((q.fieldType === 'select' || q.fieldType === 'single') && q.options?.length) {
              <div class="icd__options" role="radiogroup" [attr.aria-label]="q.name">
                @for (opt of q.options; track opt.label) {
                  <paper-button
                    buttonRole="radio"
                    class="icd__option"
                    intent="secondary"
                    size="sm"
                    [class.selected]="isOptionSelected(q.name, opt.label)"
                    (click)="setAnswer(q.name, opt.label)"
                    [ariaChecked]="isOptionSelected(q.name, opt.label)">
                    {{ opt.label }}
                    @if (opt.pricePence) {
                      <span class="icd__option-price">+{{ fmtPrice(opt.pricePence) }}</span>
                    }
                  </paper-button>
                }
              </div>
            } @else if (q.fieldType === 'multi' && q.options?.length) {
              <div class="icd__options" role="group" [attr.aria-label]="q.name">
                @for (opt of q.options; track opt.label) {
                  <paper-button
                    class="icd__option"
                    intent="secondary"
                    size="sm"
                    [class.selected]="isOptionSelected(q.name, opt.label)"
                    (click)="toggleMultiAnswer(q.name, opt.label)"
                    [ariaPressed]="isOptionSelected(q.name, opt.label)">
                    {{ opt.label }}
                    @if (opt.pricePence) {
                      <span class="icd__option-price">+{{ fmtPrice(opt.pricePence) }}</span>
                    }
                  </paper-button>
                }
              </div>
            } @else {
              <input
                type="text"
                class="icd__text-input"
                [placeholder]="'catering.customer.item_customise.enter_placeholder' | transloco"
                [value]="getAnswer(q.name)"
                (input)="setAnswer(q.name, $any($event.target).value)" />
            }
          </div>
        }

        <!-- Quantity -->
        <div class="icd__qty-section">
          <span class="icd__qty-label">{{ 'catering.customer.item_customise.quantity' | transloco }}</span>
          <div class="icd__qty-control">
            <paper-button
              class="icd__qty-button"
              intent="secondary"
              size="md"
              shape="icon"
              icon="minus"
              [ariaLabel]="'catering.customer.item_customise.decrease' | transloco"
              (click)="adjustQty(-1)"
              [disabled]="qty() <= 1" />
            <span class="icd__qty-num">{{ qty() }}</span>
            <paper-button
              class="icd__qty-button"
              intent="secondary"
              size="md"
              shape="icon"
              icon="plus"
              [ariaLabel]="'catering.customer.item_customise.increase' | transloco"
              (click)="adjustQty(1)"
              [disabled]="item().maxQuantity ? qty() >= item().maxQuantity! : false" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="icd__footer">
        <paper-button intent="ghost" size="md" (click)="onCancel()">{{ 'catering.customer.item_customise.cancel' | transloco }}</paper-button>
        <paper-button
          class="icd__confirm"
          intent="primary"
          size="md"
          [block]="true"
          [disabled]="!isValid()"
          (click)="onConfirm()">
          {{ confirmLabel() }}
        </paper-button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }

    .icd__backdrop {
      position: absolute;
      inset: 0;
      background: var(--scrim);
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
    }

    .icd {
      position: relative;
      width: 400px;
      max-width: 100vw;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      background: var(--card-bg-solid);
      border-radius: var(--radius) var(--radius) 0 0;
      overflow: hidden;
      animation: icd-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes icd-slide-up {
      from { transform: translateY(100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    .icd__hero {
      height: 200px;
      background: var(--surface-3);
      overflow: hidden;

      img { width: 100%; height: 100%; object-fit: cover; display: block; }
    }

    .icd__body {
      flex: 1;
      overflow-y: auto;
      padding: 16px 20px;
    }

    .icd__name {
      font-size: 20px; /* design flow-item: item title leads the drawer */
      font-weight: 700;
      color: var(--text);
      margin: 0 0 4px;
    }

    .icd__desc {
      font-size: 13px;
      color: var(--text-secondary);
      margin: 0 0 8px;
      line-height: 1.4;
    }

    .icd__price-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
    }

    .icd__price {
      font-size: 15px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    /* ── Questions ──────────────────── */

    .icd__question {
      margin-bottom: 16px;
    }

    .icd__q-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: var(--text);
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .icd__required { color: var(--danger); }

    .icd__options {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .icd__option {
      /* design flow-item: customisation options render as pill chips */
      --im-button-radius: var(--radius-pill, 999px);
      --im-button-padding-x: 16px;
      --im-button-padding-y: 0;
      --im-button-font-size: 13px;
      --im-button-font-weight: 500;
      --im-button-label-gap: 6px;
      --surface-raised: var(--surface-2);
      --surface-hover: var(--surface-2);
      --border-default: var(--border-low);
      --border-strong: var(--border-med);
      --im-button-text: var(--text-secondary);
      --im-button-text-hover: var(--text);

      &.selected {
        --im-button-text: var(--color-primary);
        --im-button-text-hover: var(--color-primary);
        --border-default: var(--color-primary);
        --border-strong: var(--color-primary);
        --surface-raised: color-mix(in srgb, var(--color-primary) 8%, transparent);
        --surface-hover: color-mix(in srgb, var(--color-primary) 8%, transparent);
        --im-button-font-weight: 600;
      }
    }

    .icd__option-price {
      font-size: 11px;
      font-weight: 500;
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
    }

    .icd__text-input {
      width: 100%;
      padding: 10px 12px;
      font-size: 13px;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      transition: border-color 0.15s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--color-primary); }
    }

    /* ── Quantity ───────────────────── */

    .icd__qty-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 0;
      border-top: 1px solid var(--border-low);
      margin-top: 8px;
    }

    .icd__qty-label {
      font-size: 13px;
      font-weight: 500;
      color: var(--text);
    }

    .icd__qty-control {
      display: flex;
      align-items: center;
      gap: 0;
    }

    .icd__qty-button {
      --radius-sm: var(--radius-pill);
    }

    .icd__qty-num {
      width: 40px;
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    /* ── Footer ────────────────────── */

    .icd__footer {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-top: 1px solid var(--border-low);
    }

    .icd__confirm {
      flex: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      .icd { animation: none; }
      .icd__option { transition: none; }
    }
  `],
})
export class ItemCustomiseDialogComponent {
  readonly item = input.required<CateringMenuItem>();
  readonly confirmed = output<{
    item: CateringMenuItem;
    quantity: number;
    answers: { questionName: string; answer: string }[];
  }>();
  readonly cancelled = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected fmtPrice(pence: number): string {
    return formatCurrencyMinor(pence, this.item().currency || 'GBP');
  }

  // ── Local state ────────────────────────────────

  protected readonly qty = signal(1);
  private readonly answers = signal<Map<string, string>>(new Map());

  // ── Computed ───────────────────────────────────

  protected readonly isValid = computed(() => {
    const questions = this.item().questions;
    if (!questions?.length) return true;

    // Check all required questions have answers
    return questions
      .filter(q => q.required)
      .every(q => {
        const answer = this.answers().get(q.name);
        return answer && answer.trim().length > 0;
      });
  });

  protected readonly totalLabel = computed(() => {
    this.locale.locale();
    const price = this.item().costPence;
    if (!price) return this.transloco.translate('catering.customer.item_customise.add_to_order');
    return formatCurrencyMinor(price * this.qty(), this.item().currency || 'GBP');
  });

  protected readonly confirmLabel = computed(() => {
    this.locale.locale();
    const q = this.qty();
    const total = this.totalLabel();
    return q > 1
      ? this.transloco.translate('catering.customer.item_customise.confirm_multi', { qty: q, total })
      : this.transloco.translate('catering.customer.item_customise.confirm_single', { total });
  });

  // ── Methods ────────────────────────────────────

  protected getAnswer(questionName: string): string {
    return this.answers().get(questionName) ?? '';
  }

  protected setAnswer(questionName: string, answer: string): void {
    this.answers.update(map => {
      const next = new Map(map);
      next.set(questionName, answer);
      return next;
    });
  }

  /**
   * Multi-select answers are stored as a comma-separated string in the same
   * Map as single-select answers, so the existing answer payload stays
   * compatible with the order mutation.
   */
  protected isOptionSelected(questionName: string, optionLabel: string): boolean {
    const raw = this.getAnswer(questionName);
    if (!raw) return false;
    return raw.split(',').map(s => s.trim()).includes(optionLabel);
  }

  protected toggleMultiAnswer(questionName: string, optionLabel: string): void {
    const current = this.getAnswer(questionName)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const idx = current.indexOf(optionLabel);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(optionLabel);
    this.setAnswer(questionName, current.join(', '));
  }

  protected adjustQty(delta: number): void {
    this.qty.update(q => Math.max(1, q + delta));
  }

  protected onConfirm(): void {
    const answerList = Array.from(this.answers().entries())
      .filter(([_, v]) => v.trim())
      .map(([questionName, answer]) => ({ questionName, answer }));

    this.confirmed.emit({
      item: this.item(),
      quantity: this.qty(),
      answers: answerList,
    });
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }
}
